/**
 * Chats from the classic Ask Addy, listed in the v2 chats drawer and opened in the v2 chat view.
 * They stay where the classic chat saved them: the sidebar chat history API, or this browser for
 * the oldest ones. A follow-up continues one as a v2 conversation whose thread id names the old
 * chat, and its first question carries the old conversation so the agent can follow on from it.
 */
import SidebarChatHistoryModel from "../models/sidebarChatHistoryModel";
import { defaultThreadId } from "../models/askAddyV2";
import { stripCitationMarkers } from "../helpers/citations";

const LEGACY_INDEX_KEY = "addy-ai-sidebar-chats";
const LEGACY_PAGE_SIZE = 20;
const LEGACY_THREAD_MARKER = ":classic-";
const LEGACY_MESSAGE_ID_PREFIX = "legacy-";
// Enough of a long old chat to follow on from; its newest turns are the ones kept.
const EARLIER_CHAT_MAX_CHARS = 20000;
const EARLIER_CHAT_START = "[Earlier chat from the classic Ask Addy, which this question follows on from. Oldest first:";
const EARLIER_CHAT_END = "[End of the earlier chat]";
const EARLIER_CHAT_BLOCK = /\n*\[Earlier chat from the classic Ask Addy[\s\S]*?\[End of the earlier chat\]/;

const readLocalJson = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
};

/** The v2 conversation an old chat continues as, in the open loan's (or scenario's, or general) context. */
export const legacyChatThreadId = (identity, chatId) => `${defaultThreadId(identity)}${LEGACY_THREAD_MARKER}${chatId}`;

/** The old chat a v2 conversation continues, or null for any other conversation. */
export const legacyChatIdOf = (threadId) => {
  const markerAt = (threadId || "").lastIndexOf(LEGACY_THREAD_MARKER);
  if (markerAt === -1) {
    return null;
  }
  return threadId.slice(markerAt + LEGACY_THREAD_MARKER.length) || null;
};

const toListItem = (isBrowserOnly) => ({ chatId, chatName, lastActive, dateStarted, loanId }) => ({
  chatId,
  title: chatName || "Chat",
  updatedAt: lastActive || dateStarted || 0,
  loanId: loanId || null,
  isBrowserOnly
});

/**
 * One page of old chats (`cursor`: the previous page's nextCursor, null for the first). The first
 * page also has the chats only this browser still has.
 */
export const listLegacyChatsPage = async (uid, cursor = null) => {
  const { chats: savedChats, nextCursor } = await SidebarChatHistoryModel.listChatsPage(uid, { limit: LEGACY_PAGE_SIZE, cursor });
  const browserChats = cursor
    ? []
    : Object.entries(readLocalJson(LEGACY_INDEX_KEY) || {})
        .filter(([chatId]) => readLocalJson(chatId)?.messages?.length)
        .map(([chatId, chat]) => ({ ...chat, chatId }));

  return {
    chats: [...savedChats.map(toListItem(false)), ...browserChats.map(toListItem(true))].filter(({ chatId }) => chatId),
    nextCursor
  };
};

/** Another page added to the old chats already listed; a chat saved on the server wins over this browser's copy. */
export const mergeLegacyChats = (listed, incoming) => {
  const byId = new Map();
  [...listed, ...incoming].forEach((chat) => {
    const current = byId.get(chat.chatId);
    if (!current || current.isBrowserOnly) {
      byId.set(chat.chatId, chat);
    }
  });
  return [...byId.values()];
};

const renameInBrowser = (chatId, title) => {
  const index = readLocalJson(LEGACY_INDEX_KEY);
  if (index?.[chatId]) {
    window.localStorage.setItem(LEGACY_INDEX_KEY, JSON.stringify({ ...index, [chatId]: { ...index[chatId], chatName: title } }));
  }
  const chat = readLocalJson(chatId);
  if (chat) {
    window.localStorage.setItem(chatId, JSON.stringify({ ...chat, chatName: title }));
  }
};

/** Renames an old chat where the classic chat history renames it: the history API, and this browser's copy. */
export const renameLegacyChat = async (uid, { chatId, isBrowserOnly }, title) => {
  if (!isBrowserOnly && !(await SidebarChatHistoryModel.renameChat(uid, chatId, title))) {
    throw new Error("Couldn't rename this chat");
  }
  renameInBrowser(chatId, title);
};

/** An old chat with its messages: from the history API, else from this browser. */
export const loadLegacyChat = async (uid, chatId) => {
  const savedChat = uid ? await SidebarChatHistoryModel.getChat(uid, chatId) : null;
  if (savedChat?.messages?.length) {
    return savedChat;
  }
  return readLocalJson(chatId);
};

/** Old messages as v2 messages. Their ids mark them, so the first follow-up knows to carry them. */
export const legacyChatToMessages = (messages = []) =>
  messages
    .filter(({ type, message } = {}) => (type === "user" || type === "assistant") && typeof message === "string" && message.trim())
    .map(({ type, message, citations }, index) => ({
      id: `${LEGACY_MESSAGE_ID_PREFIX}${index}`,
      role: type,
      // The classic sources aren't carried over, so their [N] markers would point at nothing.
      content: [{ type: "text", text: type === "assistant" ? stripCitationMarkers(message, citations || []) : message }]
    }));

export const isLegacyMessage = ({ id }) => String(id || "").startsWith(LEGACY_MESSAGE_ID_PREFIX);

/** True when everything before the question is the old chat: the follow-up that must carry it. */
export const isFirstLegacyFollowUp = (earlierMessages) => earlierMessages.length > 0 && earlierMessages.every(isLegacyMessage);

const keepNewest = (lines) => {
  const firstKept = lines.findIndex((_, index) => lines.slice(index).join("\n\n").length <= EARLIER_CHAT_MAX_CHARS);
  if (firstKept === -1) {
    return lines.slice(-1).map((line) => line.slice(-EARLIER_CHAT_MAX_CHARS));
  }
  return lines.slice(firstKept);
};

/** The question, then the old chat (`turns`: `{ role, text }`) for the agent to follow on from. */
export const withEarlierChat = (question, turns) => {
  const lines = turns
    .filter(({ text }) => text)
    .map(({ role, text }) => `${role === "user" ? "Loan officer" : "Assistant"}: ${text}`);
  if (!lines.length) {
    return question;
  }
  return `${question}\n\n${EARLIER_CHAT_START}\n${keepNewest(lines).join("\n\n")}\n${EARLIER_CHAT_END}`;
};

/** A saved question without the old chat it carried: the loan officer sees only what they asked. */
export const stripEarlierChat = (text) => (text || "").replace(EARLIER_CHAT_BLOCK, "");
